import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { motion } from "framer-motion";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import style from "./style";
import { useTranslation } from "react-i18next";
import { useTripContext } from "../../context/tripsContext";
import { tripsService } from "../../api/tripsService/tripsServices";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { useParams } from "react-router-dom";
import { useAuth } from "../../utils/hooks/useAuth";
import { TripDetails } from "../../types/api";

interface CardWithDialogProps {
  tripDetails: TripDetails;
}

const CardWithDialog: React.FC<CardWithDialogProps> = ({tripDetails}) => {
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 700;
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tripAcceptance, setTripAcceptance } = useTripContext();
  const {businessProfile} = useBusinessProfile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const { id } = useParams<{ id: string }>();
 


  if (!user || !id) {
    console.error("Required data is missing:", { user, id });
    return null; // Render nothing if critical data is missing
  }

  const handleDialogOpen = (selectedAction: "approve" | "reject") => {
    setAction(selectedAction);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {

    setAction(null);
    setDialogOpen(false);
  };


 
  const handleConfirmAction = async () => {
    if (!id || !action) {
      console.error("Trip ID or Action missing:", { id, action });
      return;
    }

    const newApprovalStatus = action === "approve" ? "APPROVED" : "REJECTED";
    try {
      await tripsService.updateTripAcceptance(Number(id), newApprovalStatus, user?.fullName);
      setTripAcceptance({
        ...tripAcceptance,
        approvalStatus: newApprovalStatus,
        approvalStatusUpdate: new Date().toISOString(),
        approvalStatusUser: user?.fullName,
      });
    } catch (error) {
      console.error("Error updating trip acceptance:", error);
    } finally {
      handleDialogClose();
    }
  };


  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card
        sx={{
          ...style.cardWrapper,
          flexDirection: isMobile ? "column" : "row",
          maxWidth: isMobile ? "327px" : "700px",
          height: isMobile ? "auto" : "60px",
          marginTop: isMobile ? "24px" : "",
        }}
      >
        <Typography variant="body1">
          {t("screens.trips.tripDialog.doYouWantTo")}{" "}
          <strong>{t("screens.trips.tripDialog.approve")}</strong>{" "}
          {t("screens.trips.tripDialog.thisTrip")}
        </Typography>
        <Box
          sx={{
            ...style.box1,
            flexDirection: isMobile ? "column" : "row",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Box
            sx={{
              ...style.box2,
              flexDirection: isMobile ? "row-reverse" : "row",
            }}
          >
            <Button
              fullWidth={isMobile}
              variant="outlined"
              onClick={() => handleDialogOpen("reject")}
              sx={style.buttonOutline}
              disabled={!(tripDetails?.status==="ASSIGNED" || tripDetails?.status === "ALLOCATED" )}
              >
              {isMobile
                ? t("screens.trips.tripDialog.reject2")
                : t("screens.trips.tripDialog.rejectTrip")}
            </Button>
            <Button
              fullWidth={isMobile}
              variant="contained"
              color="primary"
              onClick={() => handleDialogOpen("approve")}
            >
              {isMobile
                ? t("screens.trips.tripDialog.approve2")
                : t("screens.trips.tripDialog.approveTrip")}
            </Button>
          </Box>
        </Box>
      </Card>
    </motion.div>

    {/* Dialog di conferma */}
    <Dialog open={dialogOpen} onClose={handleDialogClose}>
      <DialogTitle>
        {action === "approve"
          ? t("screens.trips.tripDialog.tripApproval")
          : t("screens.trips.tripDialog.tripRejection")}
      </DialogTitle>
      <DialogContent>
        <Typography>
      
            {t("screens.trips.tripDialog.saveOrDiscardChanges")}
          
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} variant="outlined"  >
          {t("screens.trips.tripDialog.cancel")}
        </Button>
        <Button onClick={handleConfirmAction} color="primary" variant="contained">
          {action === "approve"
            ? t("screens.trips.tripDialog.save")
            : t("screens.trips.tripDialog.reject2")}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default CardWithDialog;
